import { createFormContext, schemaResolver, useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import type { Mail } from 'modules/Mails/hooks';
import type { MailComposerSource } from 'modules/Mails/types';
import posthog from 'posthog-js';
import { useState } from 'react';
import { captureEvent } from 'shared/analytics/capture-event';
import { createSendEmailSchema, type SendEmail, useSendEmail } from 'shared/hooks/use-send-email';
import { getSessionStorageItem } from 'shared/utils/session-storage';

import { getStorageKey, usePersistedDraft } from './use-persisted-draft';

export const [SendEmailFormProvider, useSendEmailFormContext] = createFormContext<SendEmail>();

const BASE_STORAGE_KEY = 'new-mail-form';

type UseNewMailFormOptions = {
  source: MailComposerSource;
  isAiAssisted?: boolean;
  onSuccess?: () => void;
  threadId?: string;
  debtorMail?: string;
  debtorId?: string;
  draftMail?: Mail | null;
};

export const useNewMailForm = ({
  source,
  isAiAssisted = false,
  onSuccess = () => modals.closeAll(),
  threadId,
  debtorMail,
  debtorId,
  draftMail,
}: UseNewMailFormOptions) => {
  const { sendEmail, isSendEmailLoading } = useSendEmail();
  const { draft, setDraft } = usePersistedDraft(debtorId);

  const [attachments, setAttachments] = useState<Array<File>>([]);

  const storageKey = getStorageKey(BASE_STORAGE_KEY, debtorId);

  // MailDetailComposer (replies) always passes a draftMail (Mail | null); NewMail never does.
  // Use that, not threadId, to detect "reply" — threadId becomes non-null on NewMail too
  // once a draft/thread is speculatively created, but we still want to persist there.
  const persist = draftMail === undefined;
  const storedValues = persist ? getSessionStorageItem<Partial<SendEmail>>(storageKey, {}) : {};

  const baseValues = {
    toMail: storedValues.toMail?.length ? storedValues.toMail : debtorMail ? [debtorMail] : [],
    ccMail: storedValues.ccMail ?? [],
    bccMail: storedValues.bccMail ?? [],
    subject: storedValues.subject ?? '',
    body: storedValues.body ?? '',
  };

  const form = useForm<SendEmail>({
    initialValues: {
      toMail: draftMail?.toMail ?? baseValues.toMail,
      ccMail: draftMail?.cc ?? baseValues.ccMail,
      bccMail: draftMail?.bcc ?? baseValues.bccMail,
      subject: draftMail?.subject ?? baseValues.subject,
      body: draftMail?.body ?? baseValues.body,
    },
    validate: schemaResolver(createSendEmailSchema, { sync: true }),
    onValuesChange: values => {
      if (persist) window.sessionStorage.setItem(storageKey, JSON.stringify(values));
    },
  });

  const handleSend = form.onSubmit(values => {
    sendEmail(
      {
        body: values.body,
        subject: values.subject,
        toMail: values.toMail,
        ccMail: values.ccMail,
        bccMail: values.bccMail,
        attachments,
        threadId: threadId ?? draft?.threadId,
        draftId: persist ? draft?.id : draftMail?.id,
      },
      {
        onSuccess: () => {
          captureEvent(posthog, 'mail:sent', {
            source,
            recipients_count: values.toMail.length + values.ccMail.length + values.bccMail.length,
            has_attachments: attachments.length > 0,
            is_ai_assisted: isAiAssisted,
          });

          if (persist) {
            window.sessionStorage.removeItem(storageKey);
            setDraft(null);
          }
          onSuccess();
        },
      },
    );
  });

  return {
    form,
    handleSend,
    isSendEmailLoading,
    attachments,
    setAttachments,
    initialBody: draftMail?.body ?? storedValues.body,
    draft,
    setDraft,
  };
};
